import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { finalize } from 'rxjs';
import { ButtonPadraoComponent } from '../../../../shared/components/button-padrao/button-padrao.component';
import { FormFieldComponent } from '../../../../shared/components/form-field/form-field.component';
import { SelectOneComponent } from '../../../../shared/components/select-one/select-one.component';
import { SlideTogglePadraoComponent } from '../../../../shared/components/slide-toggle-padrao/slide-toggle-padrao.component';
import { MsgService } from '../../../../shared/service/msg.service';
import { FluxoService } from '../../../../shared/service/fluxo-service';
import { EtapaService } from '../../../../shared/service/etapa-service';
import { FluxoSelect } from '../../../../shared/model/fluxo/fluxo-select-model';
import { EtapaBusca } from '../../../../shared/model/etapa/etapa-busca-model';
import { CanalInatividadePeriodo } from '../../../../shared/model/canal/canal-inatividade-periodo.model';
import { TipoAcaoInatividadeEnum } from '../../../../shared/enum/tipo-acao-inatividade.enum';

interface OpcaoTipoAcao {
  valor: TipoAcaoInatividadeEnum;
  label: string;
}

@Component({
  selector: 'app-inatividade-canal',
  standalone: true,
  imports: [
    CommonModule,
    ButtonPadraoComponent,
    FormFieldComponent,
    SelectOneComponent,
    SlideTogglePadraoComponent
  ],
  templateUrl: './inatividade-canal.component.html',
  styleUrl: './inatividade-canal.component.css'
})
export class InatividadeCanalComponent {

  @Input() periodos: CanalInatividadePeriodo[] = [];
  @Output() periodosChange = new EventEmitter<CanalInatividadePeriodo[]>();

  @Input() inatividadeAtiva = false;
  @Output() inatividadeAtivaChange = new EventEmitter<boolean>();

  @Input() readOnly = false;

  modoCadastroAberto = false;
  periodoEmEdicaoIndex: number | null = null;
  periodoForm: CanalInatividadePeriodo = new CanalInatividadePeriodo();
  fluxos: FluxoSelect[] = [];
  etapas: EtapaBusca[] = [];
  carregandoFluxos = false;
  carregandoEtapas = false;

  readonly tiposAcao: OpcaoTipoAcao[] = [
    { valor: TipoAcaoInatividadeEnum.MENSAGEM, label: 'Mensagem' },
    { valor: TipoAcaoInatividadeEnum.FLUXO, label: 'Fluxo' }
  ];
  readonly tipoAcaoMensagem = TipoAcaoInatividadeEnum.MENSAGEM;
  readonly tipoAcaoFluxo = TipoAcaoInatividadeEnum.FLUXO;

  private readonly fluxoService = inject(FluxoService);
  private readonly etapaService = inject(EtapaService);
  private readonly msgService = inject(MsgService);

  get podeAdicionarPeriodo(): boolean {
    return !this.readOnly && this.inatividadeAtiva && !this.modoCadastroAberto;
  }

  get deveMostrarLista(): boolean {
    return this.inatividadeAtiva && this.periodos.length > 0 && !this.modoCadastroAberto;
  }

  get deveMostrarEditor(): boolean {
    return this.inatividadeAtiva && this.modoCadastroAberto;
  }

  get textoBotaoAcao(): string {
    return this.periodoEmEdicaoIndex === null ? 'Adicionar Período' : 'Editar Período';
  }

  get tipoAcaoSelecionado(): boolean {
    return this.periodoForm.tipoAcao ? this.periodoForm.tipoAcao == TipoAcaoInatividadeEnum.FLUXO ? true : false : false;
  }

  get podeSalvarPeriodo(): boolean {
    if (!this.periodoForm.tempoInatividade || !this.periodoForm.tipoAcao) {
      return false;
    }
    if (this.existePeriodoDuplicado) {
      return false;
    }

    if (this.periodoForm.tipoAcao === TipoAcaoInatividadeEnum.MENSAGEM) {
      return !!this.periodoForm.mensagem?.trim();
    }

    if (this.periodoForm.tipoAcao === TipoAcaoInatividadeEnum.FLUXO) {
      return !!this.periodoForm.fluxo?.id;
    }

    return false;
  }

  get existePeriodoDuplicado(): boolean {
    return this.verificarPeriodoDuplicado(this.periodoForm.tempoInatividade, this.periodoForm.tipoAcao, this.periodoEmEdicaoIndex);
  }

  alternarInatividade(valor: boolean): void {
    this.inatividadeAtiva = valor;
    this.inatividadeAtivaChange.emit(valor);

    if (!valor) {
      this.fecharEditor();
    }
  }

  alternarTipoAcao(valor: boolean): void {
    this.selecionarTipoAcao(valor ? { valor: TipoAcaoInatividadeEnum.FLUXO, label: 'Fluxo' } : { valor: TipoAcaoInatividadeEnum.MENSAGEM, label: 'Mensagem' });
  }

  adicionarPeriodo(): void {
    this.periodoEmEdicaoIndex = null;
    this.periodoForm = new CanalInatividadePeriodo();
    this.periodoForm.tipoAcao = TipoAcaoInatividadeEnum.MENSAGEM;
    this.periodoForm.mensagem = ""
    this.etapas = [];
    this.modoCadastroAberto = true;
  }

  editarPeriodo(indicePeriodo: number): void {
    const periodo = this.periodos[indicePeriodo];
    if (!periodo) {
      return;
    }

    this.periodoEmEdicaoIndex = indicePeriodo;
    this.periodoForm = this.clonarPeriodo(periodo);
    this.modoCadastroAberto = true;

    if (this.periodoForm.tipoAcao === TipoAcaoInatividadeEnum.FLUXO && this.periodoForm.fluxo?.id) {
      this.carregarFluxos(() => this.carregarEtapas(this.periodoForm.fluxo?.id ?? null));
    }
  }

  removerPeriodo(indicePeriodo: number): void {
    this.periodos = this.periodos.filter((_, indice) => indice !== indicePeriodo);
    this.periodosChange.emit([...this.periodos]);
  }

  cancelarEdicao(): void {
    this.fecharEditor();
  }

  salvarPeriodo(): void {
    if (!this.podeSalvarPeriodo) {
      this.msgService.msgAdvertencia(this.obterMensagemValidacao());
      return;
    }

    const periodoSalvo = this.clonarPeriodo(this.periodoForm);

    if (this.periodoEmEdicaoIndex === null) {
      this.periodos = [...this.periodos, periodoSalvo];
    } else {
      this.periodos = this.periodos.map((periodo, indice) => indice === this.periodoEmEdicaoIndex ? periodoSalvo : periodo);
    }
    this.periodos = this.ordenarPeriodosAscendente(this.periodos);

    this.periodosChange.emit([...this.periodos]);
    this.fecharEditor();
  }

  selecionarTipoAcao(tipo: OpcaoTipoAcao | null): void {
    this.periodoForm.tipoAcao = tipo?.valor;
    this.periodoForm.mensagem = tipo?.valor === TipoAcaoInatividadeEnum.MENSAGEM ? this.periodoForm.mensagem : '';
    this.periodoForm.fluxo = undefined;
    this.periodoForm.etapa = undefined;
    this.etapas = [];

    if (tipo?.valor === TipoAcaoInatividadeEnum.FLUXO) {
      this.carregarFluxos();
    }
  }

  selecionarFluxo(fluxo: FluxoSelect | null): void {
    this.periodoForm.fluxo = fluxo ?? undefined;
    this.periodoForm.etapa = undefined;
    this.carregarEtapas(fluxo?.id ?? null);
  }

  selecionarEtapa(etapa: EtapaBusca | null): void {
    this.periodoForm.etapa = etapa ?? undefined;
  }

  carregarFluxos(callback?: () => void): void {
    if (this.fluxos.length > 0) {
      callback?.();
      return;
    }

    this.carregandoFluxos = true;
    this.fluxoService.buscarFluxosSelectQuePossueEtapa()
      .pipe(finalize(() => this.carregandoFluxos = false))
      .subscribe({
        next: res => {
          this.fluxos = res ?? [];
          callback?.();
        },
        error: () => this.msgService.msgErro('Erro ao carregar fluxos')
      });
  }

  carregarEtapas(fluxoId: number | null, callback?: () => void): void {
    if (!fluxoId) {
      this.etapas = [];
      callback?.();
      return;
    }

    this.carregandoEtapas = true;
    this.etapaService.buscar(fluxoId)
      .pipe(finalize(() => this.carregandoEtapas = false))
      .subscribe({
        next: res => {
          this.etapas = res ?? [];
          callback?.();
        },
        error: () => {
          this.etapas = [];
          this.msgService.msgErro('Erro ao carregar etapas');
        }
      });
  }

  trackByPeriodo(index: number, periodo: CanalInatividadePeriodo): string {
    return `${index}-${periodo.id ?? 'novo'}-${periodo.tempoInatividade ?? ''}`;
  }

  formatarTipo(periodo: CanalInatividadePeriodo): string {
    return periodo.tipoAcao === TipoAcaoInatividadeEnum.FLUXO ? 'Fluxo' : 'Mensagem';
  }

  obterConteudoPeriodo(periodo: CanalInatividadePeriodo): string {
    if (periodo.tipoAcao === TipoAcaoInatividadeEnum.FLUXO) {
      return periodo.fluxo?.nome ?? '--';
    }

    return periodo.mensagem ?? '--';
  }

  obterEtapaPeriodo(periodo: CanalInatividadePeriodo): string {
    if (periodo.tipoAcao !== TipoAcaoInatividadeEnum.FLUXO) {
      return '';
    }

    return periodo.etapa?.titulo ? `Etapa: ${periodo.etapa.titulo}` : '';
  }

  verificarPeriodoDuplicado(tempoInatividade?: string, tipoAcao?: TipoAcaoInatividadeEnum | string, indiceIgnorar: number | null = null): boolean {
    if (!tempoInatividade || !tipoAcao) {
      return false;
    }
    return this.periodos.some((periodo, indice) => {
      if (indiceIgnorar !== null && indice === indiceIgnorar) {
        return false;
      }

      return (
        periodo.tempoInatividade === tempoInatividade &&
        periodo.tipoAcao === tipoAcao
      );
    });
  }

  ordenarPeriodosAscendente(periodos: CanalInatividadePeriodo[]): CanalInatividadePeriodo[] {
    return [...periodos].sort((a, b) => {
      const tempoA = this.converterTempoParaMinutos(a.tempoInatividade);
      const tempoB = this.converterTempoParaMinutos(b.tempoInatividade);
      return tempoA - tempoB;
    });
  }

  converterTempoParaMinutos(tempo: string | null | undefined): number {
    if (!tempo) {
      return 0;
    }

    const [horas, minutos] = tempo.split(':').map(Number);
    return (horas || 0) * 60 + (minutos || 0);
  }

  fecharEditor(): void {
    this.modoCadastroAberto = false;
    this.periodoEmEdicaoIndex = null;
    this.periodoForm = new CanalInatividadePeriodo();
    this.etapas = [];
  }

  private clonarPeriodo(periodo: CanalInatividadePeriodo): CanalInatividadePeriodo {
    return {
      id: periodo.id,
      tempoInatividade: periodo.tempoInatividade,
      tipoAcao: periodo.tipoAcao,
      mensagem: periodo.mensagem,
      fluxo: periodo.fluxo ? { ...periodo.fluxo } : undefined,
      etapa: periodo.etapa ? { ...periodo.etapa } : undefined,
      ativo: periodo.ativo
    };
  }

  private obterMensagemValidacao(): string {
    if (!this.periodoForm.tempoInatividade) {
      return 'Informe o tempo de inatividade.';
    }

    if (!this.periodoForm.tipoAcao) {
      return 'Selecione o tipo da ação.';
    }

    if (this.existePeriodoDuplicado) {
      const tipoLabel = this.periodoForm.tipoAcao === TipoAcaoInatividadeEnum.FLUXO ? 'fluxo' : 'mensagem';
      return `Já existe um período de ${tipoLabel} cadastrado nesse tempo (${this.periodoForm.tempoInatividade}).`;
    }

    if (this.periodoForm.tipoAcao === TipoAcaoInatividadeEnum.MENSAGEM && !this.periodoForm.mensagem?.trim()) {
      return 'Informe a mensagem de inatividade.';
    }

    if (this.periodoForm.tipoAcao === TipoAcaoInatividadeEnum.FLUXO && !this.periodoForm.fluxo?.id) {
      return 'Selecione um fluxo.';
    }

    return 'Existem campos obrigatórios pendentes.';
  }
}
